import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { AlertTriangle, FileText, Download, DollarSign } from 'lucide-react';
import { usePOCancellation } from '@/hooks/usePOCancellation';
import { POModificationRequest, formatCurrency } from '@/lib/types/job';
import { CancellationDocument } from '@/lib/types/cancellation';

interface ClientCancellationFeeReviewCardProps {
  cancellationRequest: POModificationRequest & {
    purchase_orders: {
      po_amount_cents: number;
      po_number: string;
    };
  };
  jobPublicId?: string;
}

export const ClientCancellationFeeReviewCard = ({
  cancellationRequest,
  jobPublicId,
}: ClientCancellationFeeReviewCardProps) => {
  const navigate = useNavigate();
  const [showDisputeDialog, setShowDisputeDialog] = useState(false);
  const [showAcceptDialog, setShowAcceptDialog] = useState(false);
  const [disputeReason, setDisputeReason] = useState('');
  const [disputeDetails, setDisputeDetails] = useState('');
  const { submitClientApproval } = usePOCancellation();

  const poAmount = cancellationRequest.purchase_orders.po_amount_cents;
  const fee = cancellationRequest.cancellation_charge_cents || 0;
  const refund = poAmount - fee;

  const handleAccept = () => {
    submitClientApproval.mutate(
      {
        cancellationRequestId: cancellationRequest.id,
        decision: 'accept',
      },
      {
        onSuccess: () => {
          setShowAcceptDialog(false);
          // Redirect to job details page to show updated status
          if (jobPublicId) {
            navigate(`/jobs/${jobPublicId}`);
          }
        },
      }
    );
  };

  const handleDispute = () => {
    if (disputeReason.trim() && disputeDetails.trim().length >= 50) {
      submitClientApproval.mutate(
        {
          cancellationRequestId: cancellationRequest.id,
          decision: 'dispute',
          disputeReason,
          disputeDetails,
        },
        {
          onSuccess: () => {
            setShowDisputeDialog(false);
            setDisputeReason('');
            setDisputeDetails('');
          },
        }
      );
    }
  };

  return (
    <>
      <Alert variant="destructive" className="mb-4">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          <strong>Action Required:</strong> The vendor has proposed a cancellation fee for your cancellation request. Please review and respond.
        </AlertDescription>
      </Alert>

      <Card className="border-2 border-destructive">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Vendor Cancellation Fee Proposal</CardTitle>
            <Badge variant="destructive">Awaiting Your Response</Badge>
          </div>
          <CardDescription>
            PO {cancellationRequest.purchase_orders.po_number}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-3 gap-4 p-4 bg-muted rounded-lg">
            <div>
              <p className="text-sm text-muted-foreground">Original Amount</p>
              <p className="text-lg font-semibold">{formatCurrency(poAmount)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Cancellation Fee</p>
              <p className="text-lg font-semibold text-destructive">
                {formatCurrency(fee)}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Your Refund</p>
              <p className="text-lg font-semibold text-green-600">
                {formatCurrency(refund)}
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <h4 className="font-semibold">Vendor's Explanation</h4>
            <Card>
              <CardContent className="pt-4">
                <p className="text-sm whitespace-pre-wrap">
                  {cancellationRequest.cancellation_fee_explanation}
                </p>
              </CardContent>
            </Card>
          </div>

          {cancellationRequest.cancellation_supporting_documents && 
           Array.isArray(cancellationRequest.cancellation_supporting_documents) &&
           cancellationRequest.cancellation_supporting_documents.length > 0 && (
            <div className="space-y-2">
              <h4 className="font-semibold">Supporting Documents</h4>
              <div className="space-y-2">
                {(cancellationRequest.cancellation_supporting_documents as CancellationDocument[]).map((doc, index) => (
                  <Card key={index}>
                    <CardContent className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <FileText className="h-5 w-5 text-muted-foreground" />
                        <div>
                          <p className="text-sm font-medium">{doc.file_name}</p>
                          <p className="text-xs text-muted-foreground">
                            {doc.file_type} • {(doc.size_bytes / 1024).toFixed(1)} KB
                          </p>
                        </div>
                      </div>
                      <Button variant="outline" size="sm" asChild>
                        <a href={doc.file_url} download>
                          <Download className="h-4 w-4 mr-2" />
                          Download
                        </a>
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <Button
              variant="default"
              className="flex-1"
              onClick={() => setShowAcceptDialog(true)}
            >
              Accept Fee & Complete Cancellation
            </Button>
            <Button
              variant="destructive"
              className="flex-1"
              onClick={() => setShowDisputeDialog(true)}
            >
              Dispute This Fee
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Accept Confirmation Dialog */}
      <Dialog open={showAcceptDialog} onOpenChange={setShowAcceptDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Fee Acceptance</DialogTitle>
            <DialogDescription>
              You are about to accept the cancellation fee and complete the cancellation.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                <strong>This action cannot be undone.</strong> By accepting:
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>You will receive refund: {formatCurrency(refund)}</li>
                  <li>The purchase order will be cancelled</li>
                  <li>The job will be closed</li>
                </ul>
              </AlertDescription>
            </Alert>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowAcceptDialog(false)}
              disabled={submitClientApproval.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAccept}
              disabled={submitClientApproval.isPending}
            >
              {submitClientApproval.isPending ? 'Processing...' : 'Confirm & Accept'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dispute Dialog */}
      <Dialog open={showDisputeDialog} onOpenChange={setShowDisputeDialog}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Dispute Cancellation Fee</DialogTitle>
            <DialogDescription>
              Provide details about why you believe the cancellation fee is unreasonable.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                Your dispute will be reviewed by an administrator. The purchase order will remain active until the dispute is resolved.
              </AlertDescription>
            </Alert>

            <div className="space-y-2">
              <Label htmlFor="dispute-reason">Dispute Category *</Label>
              <Textarea
                id="dispute-reason"
                placeholder="e.g., Fee is excessive, No work was performed, etc."
                value={disputeReason}
                onChange={(e) => setDisputeReason(e.target.value)}
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="dispute-details">Detailed Explanation * (min 50 characters)</Label>
              <Textarea
                id="dispute-details"
                placeholder="Provide a detailed counter-argument to the vendor's explanation. Include any evidence or reasoning for why the fee should be reduced or eliminated..."
                value={disputeDetails}
                onChange={(e) => setDisputeDetails(e.target.value)}
                rows={6}
                className="resize-none"
              />
              <p className="text-sm text-muted-foreground">
                {disputeDetails.length} / 50 characters minimum
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDisputeDialog(false)}
              disabled={submitClientApproval.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDispute}
              disabled={
                !disputeReason.trim() ||
                disputeDetails.trim().length < 50 ||
                submitClientApproval.isPending
              }
            >
              {submitClientApproval.isPending ? 'Submitting...' : 'File Dispute'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
